<template>
    <Layout :localeData="localeData">
        <main
            :class="$style.main"
            data-interface-section="listing"
        >
            <div :class="$style.header">
                <div :class="$style.caption">
                    <Typography
                        is="h1"
                        size="m"
                        type="header"
                        responsive
                    >
                        {{ tr('searchPage.title_with_query') }}
                    </Typography>
                    <Typography
                        is="h1"
                        size="m"
                        type="header"
                        responsive
                        :class="$style.search"
                    >
                        {{ searchQuery }}
                    </Typography>
                </div>
                <CategoriesList :subCategories="categories" />
            </div>

            <div
                v-if="!isLoading && games.length === 0 && categories.length === 0"
                :class="$style.empty_wrapper_text"
            >
                <div :class="$style.no_results_img_wrapper">
                    <img
                        :src="EyesImg"
                        alt="no_results"
                        loading="lazy"
                        :class="$style.no_results_img"
                    />
                </div>
                <div :class="$style.no_results_text">
                    <Typography
                        type="label"
                        size="m"
                        :responsive="false"
                        :class="$style.no_results_desc"
                    >
                        {{ $tr('searchPage.no_results') }}
                    </Typography>
                </div>
            </div>

            <div :class="$style.grid">
                <template v-if="games.length > 0">
                    <GameCard
                        v-for="(game, cardIndex) in games"
                        :key="game.hru"
                        :data-interface-target="`game-number-${cardIndex + 1}`"
                        :game="game"
                        :ctrContext="searchCtrContexts[cardIndex]"
                        responsive
                        withBadges
                        :targetLink="undefined"
                        withScaleOnHover
                        type="rectangle"
                        :autoplayVideoInViewport="cardIndex === 0"
                        v-bind="
                            cardIndex < 3
                                ? {
                                      class: $style.card,
                                      preload: cardIndex === 0,
                                      lazy: cardIndex !== 0,
                                  }
                                : {}
                        "
                    />
                </template>

                <template v-else-if="isLoading">
                    <GameCard
                        v-for="(_, cardIndex) in placeholderPage"
                        :key="`placeholder-${cardIndex}`"
                        :game="undefined"
                        responsive
                        withBadges
                        withScaleOnHover
                        type="rectangle"
                        v-bind="
                            cardIndex < 3
                                ? {
                                      class: $style.card,
                                  }
                                : {}
                        "
                    />
                </template>

                <div
                    v-else
                    :class="$style.trending"
                    data-interface-section="trending-embed"
                >
                    <ShelfTitle
                        :title="trendingCategory.title"
                        :link="trendingCategory.location"
                        categoryId="trending_now"
                    />
                    <GeneralShelf
                        :games="trendingList"
                        :link="trendingCategory.location"
                        :gameLinkTarget="undefined"
                        :ctrContexts="trendingCtrContexts"
                        embedded
                        :categoryName="trendingCategory.title"
                    />
                </div>
            </div>

            <PromoCategories />

            <Breadcrumbs
                v-if="!appStore.isAppEnv"
                :breadcrumbs="breadcrumbs"
            />
        </main>
    </Layout>
</template>
<script setup lang="ts">
import { type ReactiveHead, useHead } from '@unhead/vue'
import { computed, onMounted, onServerPrefetch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Breadcrumbs from '@/components/Breadcrumbs.vue'
import GameCard from '@/components/GameCard/GameCard.vue'
import GeneralShelf from '@/components/GamesShelf/GeneralShelf.vue'
import ShelfTitle from '@/components/GamesShelf/ShelfTitle.vue'
import EyesImg from '@/components/images/eyes.webp'
import PromoCategories from '@/components/PromoCategories/PromoCategories.vue'
import Typography from '@/components/Typography.vue'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { useCtrListContexts } from '@/composables/use-ctr-context'
import { useSchemaOrg } from '@/composables/use-schema-org'
import { useTr } from '@/composables/use-tr'
import Layout from '@/layouts/Layout.vue'
import CategoriesList from '@/pages/Category/components/CategoriesList.vue'
import { useTrendingData } from '@/query/use-trending-data'
import { ROUTE_NAMES } from '@/router/constants'
import { SSR_QUERY } from '@/router/query-params'
import { useAppStore } from '@/store/app-store'

import { useSearchPageData } from './use-search-page-data'
const appStore = useAppStore()
const $route = useRoute()
const router = useRouter()
const tr = useTr()

const searchQuery = computed(() => ($route.query[SSR_QUERY.SEARCH_QUERY] as string) || '')
const { games, categories, pageSize, preloadSearchPageData, isLoading } = useSearchPageData({
    searchQuery,
})
const searchCtrContexts = useCtrListContexts(
    () => games.value,
    () => ({ placement: 'search', dimension1: searchQuery.value }),
)

const { currentTrending: trendingCategory, unzippedTrendingWithGames: trendingGames } = useTrendingData()
const trendingList = computed(() => trendingGames.value.slice(0, 9))
const trendingCtrContexts = useCtrListContexts(
    () => trendingList.value,
    () => ({ placement: 'trending_now' }),
)

const placeholderPage = computed(() => Array(pageSize).fill(null))
const { Organization, WebSite, WebSiteId, getFullLink, getInLanguage } = useSchemaOrg()

const currentSearchRoute = computed(
    () =>
        router.resolve({
            name: ROUTE_NAMES.SEARCH,
            query: searchQuery.value
                ? {
                      [SSR_QUERY.SEARCH_QUERY]: searchQuery.value,
                  }
                : {},
        }).fullPath,
)

const breadcrumbs = computed(() => {
    return [
        {
            title: tr('categoryPage.main_title'),
            href: router.resolve({ name: ROUTE_NAMES.MAIN }).fullPath,
        },
        {
            title: tr('searchPage.breadcrumb'),
            href: router.resolve({ name: ROUTE_NAMES.SEARCH }).fullPath,
        },
        {
            title: searchQuery.value,
            href: currentSearchRoute.value,
            disabled: true,
        },
    ]
})

const metaTitle = computed(() => {
    const base = searchQuery.value
        ? tr('searchPage.meta_title_with_query', { query: searchQuery.value })
        : tr('searchPage.meta_title')
    return base
})

const metaDescription = computed(() => {
    return searchQuery.value
        ? tr('searchPage.meta_description_with_query', { query: searchQuery.value })
        : tr('searchPage.meta_description')
})

const breadcrumbsSchema = computed(() => {
    if (!breadcrumbs.value.length) {
        return {}
    }

    const breadcrumbItems = breadcrumbs.value.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
            '@type': 'Thing',
            '@id': getFullLink(item.href),
            name: item.title,
        },
    }))

    return {
        breadcrumb: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
        },
    }
})

const organizationSchema = computed(() => {
    const currentRoute = getFullLink({
        name: ROUTE_NAMES.SEARCH,
        query: {
            [SSR_QUERY.SEARCH_QUERY]: searchQuery.value,
        },
    })

    return {
        '@context': 'https://schema.org',
        '@graph': [
            Organization.value,
            WebSite.value,
            {
                '@type': 'SearchResultsPage',
                '@id': `${currentRoute}#SearchResultsPage`,
                url: currentRoute,
                name: metaTitle.value,
                description: metaDescription.value,
                inLanguage: getInLanguage(),
                isPartOf: {
                    '@id': WebSiteId.value,
                },
                ...breadcrumbsSchema.value,
            },
        ],
    }
})

const { getCanonicalAndAlternates, getLocalizedUrls } = useLocalizedRouters()

const pageMeta = computed(() => {
    const links: ReactiveHead['link'] = [
        ...getCanonicalAndAlternates({
            mapRoute(route) {
                return {
                    ...route,
                    query: {
                        [SSR_QUERY.SEARCH_QUERY]: searchQuery.value,
                    },
                }
            },
        }),
    ]

    return {
        title: () => metaTitle.value,
        script: [
            {
                type: 'application/ld+json',
                innerHTML: organizationSchema.value,
            },
        ],
        meta: [
            {
                name: 'title',
                content: metaTitle.value,
            },
            {
                name: 'description',
                content: metaDescription.value,
            },
            {
                property: 'og:title',
                content: metaTitle.value,
            },
            {
                property: 'og:description',
                content: metaDescription.value,
            },
            {
                name: 'twitter:title',
                content: metaTitle.value,
            },
            {
                name: 'twitter:description',
                content: metaDescription.value,
            },
        ],
        link: links,
    }
})

const localeData = computed(() => {
    return {
        languageLinks: getLocalizedUrls({
            mapRoute(route) {
                return {
                    ...route,
                    query: {
                        [SSR_QUERY.SEARCH_QUERY]: searchQuery.value,
                    },
                }
            },
        }),
    }
})

useHead(pageMeta)

onServerPrefetch(async () => {
    await preloadSearchPageData()
})

onMounted(async () => {
    await preloadSearchPageData()
})
</script>
<style module>
.trending {
    grid-column-start: 1;
    grid-column-end: -1;
    grid-row-start: 5;
    background: var(--experimental-purple-graphit);
    border-radius: 16px;
    padding: 16px 12px;
    margin: 12px 0;
}

.no_results_desc {
    color: var(--white-60);
    max-width: 560px;
}

.no_results_text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 12px;
    margin-bottom: 8px;
}

.empty_wrapper_text {
    display: flex;
    align-items: center;
}
.no_results_img_wrapper {
    width: 100px;
    text-align: center;
    flex-shrink: 0;
}

.no_results_img_wrapper {
    width: 100px;
    text-align: center;
    flex-shrink: 0;
}

.no_results_img {
    width: 60px;
    height: 60px;
}

.main {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.header {
    gap: 8px;
    margin-bottom: -16rem;
}

.caption {
    display: flex;
    flex-direction: row;
}

.search {
    color: var(--electric-lime);
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-flow: dense;
    column-gap: 8px;
    row-gap: 12px;
    position: relative;
}

.card:nth-child(-n + 1) {
    grid-column: span 2;
    grid-row: span 2;
}

@media (width >= 640px) {
    .grid {
        grid-template-columns: repeat(4, 1fr);
    }

    .card:nth-child(-n + 2) {
        grid-column: span 2;
        grid-row: span 2;
    }

    .trending {
        grid-row-start: 4;
    }
}

@media (--desktop-small-plus) {
    .grid {
        grid-template-columns: repeat(6, 1fr);
    }

    .card:nth-child(-n + 3) {
        grid-column: span 2;
        grid-row: span 2;
    }

    .main {
        gap: 32px;
    }
}
</style>
